import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const holdupErrorTypeValues = [
  "HOLDUP_NOT_FOUND",
  "SCOPE_TASK_INVALID",
  "HELD_UP_TASK_INVALID",
  "RELEASE_TASK_IN_SCOPE",
  "PARTY_INSTANCE_INVALID",
  "FOLLOW_UP_DATE_INVALID",
  "RELEASE_TASK_INVALID",
  "RELEASE_KIND_MISMATCH",
  "BLOCK_TASK_NO_BLOCKED_TYPES",
  "BLOCK_TASK_INHERITED_UNSUPPORTED",
  "BLOCK_TASK_NO_TUPLE_BOUND",
  "PAIRED_HOLDUP_NOT_VISIBLE_IN_TRANSACTION",
  "HOLDUP_RELEASE_REFUSED_PAIRED_BLOCKER",
  "HOLDUP_RELEASE_READINESS_RECONCILE_FAILED",
  "NOT_IMPLEMENTED",
] as const;
const holdupErrorTypeSchema = z.enum(holdupErrorTypeValues);
const HoldupErrorType = holdupErrorTypeSchema.enum;
type HoldupErrorType = z.infer<typeof holdupErrorTypeSchema>;

export type HoldupErrors =
  | { errorType: typeof HoldupErrorType.HOLDUP_NOT_FOUND; data: { holdupId: string } }
  | { errorType: typeof HoldupErrorType.SCOPE_TASK_INVALID; data: { taskIds: string[] } }
  | { errorType: typeof HoldupErrorType.HELD_UP_TASK_INVALID; data: { taskId: string } }
  | { errorType: typeof HoldupErrorType.RELEASE_TASK_IN_SCOPE; data: { taskId: string } }
  | {
      errorType: typeof HoldupErrorType.PARTY_INSTANCE_INVALID;
      data: { responsibleParty: string; responsiblePartyOrganizationId: string | null };
    }
  | { errorType: typeof HoldupErrorType.FOLLOW_UP_DATE_INVALID; data: { followUpDate: string } }
  | {
      errorType: typeof HoldupErrorType.RELEASE_TASK_INVALID;
      data: {
        taskId: string;
        actualType: string | null;
        actualStatus: string | null;
        isDeleted: boolean | null;
      };
    }
  | {
      errorType: typeof HoldupErrorType.RELEASE_KIND_MISMATCH;
      data: {
        holdupId: string;
        releaseKind: string;
        holdupType: string;
        hasReleaseTask: boolean;
        followUpDate: string | null;
        releasedAt: string | null;
      };
    }
  | {
      errorType: typeof HoldupErrorType.BLOCK_TASK_NO_BLOCKED_TYPES;
      data: { taskEventId: string; blockTarget: string };
    }
  | {
      errorType: typeof HoldupErrorType.BLOCK_TASK_INHERITED_UNSUPPORTED;
      data: { taskEventId: string; blockedTaskTypeCount: number };
    }
  | {
      errorType: typeof HoldupErrorType.BLOCK_TASK_NO_TUPLE_BOUND;
      data: { taskEventId: string; holdupId: string; requestedTupleCount: number };
    }
  | {
      errorType: typeof HoldupErrorType.PAIRED_HOLDUP_NOT_VISIBLE_IN_TRANSACTION;
      data: { taskEventId: string; holdupId: string };
    }
  | {
      errorType: typeof HoldupErrorType.HOLDUP_RELEASE_REFUSED_PAIRED_BLOCKER;
      data: { holdupId: string; unresolvedBlockerCount: number };
    }
  | {
      errorType: typeof HoldupErrorType.HOLDUP_RELEASE_READINESS_RECONCILE_FAILED;
      data: { holdupId: string; failedTaskIds: string[] };
    }
  | { errorType: typeof HoldupErrorType.NOT_IMPLEMENTED; data: { method: string } };

export const holdupErrorDetails: Record<HoldupErrorType, ErrorDetails> = {
  HOLDUP_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Holdup not found",
  },
  SCOPE_TASK_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Holdup scope references tasks that do not exist or are soft-deleted",
  },
  HELD_UP_TASK_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Held-up task for a responsible-party holdup does not exist or is not accessible",
  },
  RELEASE_TASK_IN_SCOPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Holdup release task is also in its held-up scope (a task blocking itself)",
  },
  PARTY_INSTANCE_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Holdup responsible-party organization is invalid for the supplied party",
  },
  FOLLOW_UP_DATE_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Holdup follow-up date is not in the future",
  },
  RELEASE_TASK_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Explicit holdup release task is missing, deleted, completed, or on another project",
  },
  RELEASE_KIND_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Holdup was released under a kind its own row state cannot have produced",
  },
  BLOCK_TASK_NO_BLOCKED_TYPES: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "BLOCK_TASK attempt outcome named no blocked task types, so its paired holdup would have nothing to release it",
  },
  BLOCK_TASK_INHERITED_UNSUPPORTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "BLOCK_TASK attempt outcome declared blockTarget INHERITED, which no interpreter arm implements yet",
  },
  BLOCK_TASK_NO_TUPLE_BOUND: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "BLOCK_TASK attempt bound no blocker tuple to its paired holdup, so the transaction was rolled back rather than committing an unreleasable hold",
  },
  PAIRED_HOLDUP_NOT_VISIBLE_IN_TRANSACTION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Paired holdup was not readable from inside the transaction that declared it, so its blocker tuples were rolled back",
  },
  HOLDUP_RELEASE_READINESS_RECONCILE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "A held-up task's readiness failed to reconcile, so the hold release was rolled back. The underlying error is captured separately by the readiness service",
  },
  HOLDUP_RELEASE_REFUSED_PAIRED_BLOCKER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "This hold is released by resolving its blocker, not by releasing the hold directly. Resolve the blocking task or its blocker entry instead",
  },
  NOT_IMPLEMENTED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Holdup service method not yet implemented",
  },
};
